import React from 'react'
import { Swiper } from 'antd-mobile'
import { ResultSkinRecommendedProduct } from '../../types'
import useFeaturedProductCarouselController from './hooks/use-featured-product-carousel-controller'
import usePrefersReducedMotion from './hooks/use-prefers-reduced-motion'
import styles from './application-flow-featured-product-carousel.module.scss'

const DEFAULT_LENDING_RATE = 92
const NUMBER_FORMATTER = new Intl.NumberFormat('en-US')

interface Props {

  /** 顶部推荐接口提供的轮播产品，顺序保持接口返回顺序。 */
  products: readonly ResultSkinRecommendedProduct[]

  /** 用户申请当前轮播产品时回传其原始 ID。 */
  onApply?: (productId: string) => void

  /** 用户滑到新卡片时委托旧容器执行原有曝光上报。 */
  onExpose?: (productId: string) => void
}

const formatAmount = (value: ResultSkinRecommendedProduct['limit']): string => {
  const normalized = String(value || '')
  const amountMap: Record<string, string> = {
    1: '30,000',
    2: '50,000',
    3: '100,000',
    4: '200,000',
  }

  if (amountMap[normalized]) {
    return amountMap[normalized]
  }

  const numericValue = Number(normalized)
  return Number.isFinite(numericValue) && normalized ? NUMBER_FORMATTER.format(numericValue) : normalized || '200,000'
}

/**
 * ApplicationFlowFeaturedProductCarousel — 顶部推荐接口产品的纯展示轮播，申请和曝光均回调旧容器。
 * @returns {React.ReactElement | null} 顶部推荐产品轮播节点
 */
const ApplicationFlowFeaturedProductCarousel: React.FC<Props> = React.memo(({
  onApply,
  onExpose,
  products,
}) => {
  const controller = useFeaturedProductCarouselController({
    onExpose,
    products,
  })
  const prefersReducedMotion = usePrefersReducedMotion()

  if (!products.length) {
    return null
  }

  return (
    <section aria-label="精选推荐产品" className={styles.root}>
      <Swiper
        autoplay={products.length > 1 && !prefersReducedMotion}
        autoplayInterval={5000}
        indicator={() => null}
        loop={products.length > 1}
        onIndexChange={controller.onIndexChange}
      >
        {products.map(product => (
          <Swiper.Item key={product.id}>
            <button className={styles.card} onClick={() => onApply?.(product.id)} type="button">
              <span className={styles.banner}>
                {product.logo ? <img alt="" aria-hidden="true" height={18} src={product.logo} width={18} /> : null}
                <strong>{product.name}</strong>
                <i aria-hidden="true" />
                <span>因您资质良好，为您推荐</span>
              </span>
              <span className={styles.cardBody}>
                <small>最高可借(元)</small>
                <b>{formatAmount(product.limit)}</b>
                <em>今日放款率{product.lendingRate || DEFAULT_LENDING_RATE}%</em>
                <span className={styles.applyButton}>立即申请</span>
              </span>
            </button>
          </Swiper.Item>
        ))}
      </Swiper>
      {products.length > 1 ? (
        <div aria-label={`第${controller.activeIndex + 1}项，共${products.length}项`} className={styles.indicator}>
          {products.map((product, index) => (
            <i aria-hidden="true" className={index === controller.activeIndex ? styles.indicatorActive : undefined} key={product.id} />
          ))}
        </div>
      ) : null}
    </section>
  )
})

ApplicationFlowFeaturedProductCarousel.displayName = 'ApplicationFlowFeaturedProductCarousel'

export default ApplicationFlowFeaturedProductCarousel
